/**
 * Tipi di un run dell'Agente Analista condivisi tra backend e frontend
 * (agentanalyst.md §5, §10, §11). Nessuna dipendenza da Node.
 */

export const RUN_STATUSES = [
  'QUEUED', 'INTAKE', 'NEEDS_INPUT', 'SCOPING', 'ACQUIRING', 'PREPARING', 'COMPUTING',
  'ENRICHING', 'EXPLORING', 'DRAFTING', 'VERIFYING', 'REPORTING_LIMITS',
  'COMPLETED', 'FAILED', 'CANCELLED',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** Stati da cui un worker può far avanzare il run. */
export const ACTIVE_STATUSES: readonly RunStatus[] = [
  'QUEUED', 'INTAKE', 'SCOPING', 'ACQUIRING', 'PREPARING', 'COMPUTING', 'ENRICHING', 'EXPLORING', 'DRAFTING', 'VERIFYING', 'REPORTING_LIMITS',
];
export const FINAL_STATUSES: readonly RunStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED'];

export type SignalClass = 'STRONG' | 'EMERGING' | 'NOISE' | 'INSUFFICIENT';

/**
 * Significato di ogni classe di segnale (analysis-playbook.md → Classificazione del segnale),
 * mostrato accanto alla classe nel report e nell'interfaccia. Le classi misurano la forza
 * delle prove, non un andamento nel tempo: EMERGING non vuol dire "in crescita".
 */
export const SIGNAL_MEANING: Readonly<Record<SignalClass, string>> = {
  STRONG: 'prove solide',
  EMERGING: 'prove parziali, non indica crescita',
  NOISE: 'spiegato da un artefatto',
  INSUFFICIENT: 'dati insufficienti',
};

export type Intent = 'OVERVIEW' | 'SENTIMENT' | 'NARRATIVE' | 'VOICES' | 'SOURCES' | 'TIMELINE' | 'COMPARE' | 'FORECAST' | 'DATA_QUESTION';

/** Perimetro scelto dall'utente (§D11): l'agente non lo allarga. */
/**
 * Metriche filtrabili per fonte (Criticita_dev.md, Fase 3, 3.5–3.11; decisione D7).
 * `interactions` = commenti + like + condivisioni (le visualizzazioni non sono interazioni).
 */
export const FILTERABLE_METRICS = [
  'followers', 'likes', 'comments', 'shares', 'views', 'interactions', 'social_impressions', 'media_reach', 'media_impressions', 'visibility',
] as const;
export type FilterableMetric = (typeof FILTERABLE_METRICS)[number];

export const METRIC_LABEL: Readonly<Record<FilterableMetric, string>> = {
  followers: 'follower', likes: 'like', comments: 'commenti', shares: 'condivisioni', views: 'visualizzazioni',
  interactions: 'interazioni (commenti + like + condivisioni)', social_impressions: 'social impressions',
  media_reach: 'media reach', media_impressions: 'media impressions', visibility: 'visibility',
};

/**
 * Intervallo su una metrica, valido solo per le fonti indicate (le altre righe non sono
 * toccate). Una riga senza valore (assente o zero: per molte fonti lo zero significa "non
 * misurato") resta o esce secondo `missing`, scelto esplicitamente (D7).
 */
export interface MetricRange {
  sources: string[];
  metric: FilterableMetric;
  min: number | null;
  max: number | null;
  missing: 'exclude' | 'keep';
}

/** Filtri sulle metriche del perimetro: preset con un nome da dichiarare, o intervalli liberi. */
export interface MetricFilterSet {
  preset: string | null;
  ranges: MetricRange[];
}

export type DataScope =
  | { kind: 'pulsar'; searchId: string; from: string; to: string | null; sources?: string[]; metricFilters?: MetricFilterSet }
  | { kind: 'dataset'; datasetId: string; metricFilters?: MetricFilterSet };

export interface RunScope {
  primary: DataScope;
  /** Secondo termine di un confronto (seconda search o secondo periodo), se scelto. */
  compare?: DataScope | null;
}

export interface TimeReference {
  expression: string;
  from_utc: string | null;
  to_utc: string | null;
  role: 'main' | 'comparison';
  /**
   * `perimeter`: delimita i dati da analizzare ("ultimi 30 giorni"), l'analisi si restringe
   * a quel periodo (decisione D2). `focus`: un momento da esaminare nel contesto del periodo
   * ("il picco del 10 giugno"), l'analisi resta sull'intera finestra. Assente negli intake
   * precedenti: vale come `focus`.
   */
  kind?: 'perimeter' | 'focus';
  /** Calcolato dal codice rispetto alla finestra del perimetro, non dal modello. */
  relation?: 'within' | 'overlaps' | 'outside' | 'unresolved';
}

export interface Intake {
  intent: { primary: Intent; secondary: Intent[] };
  topic: {
    statement: string | null; keywords: string[]; keywords_reliable: boolean; entities: string[];
    /**
     * Oggetto rispetto a cui si può essere favorevoli o contrari ("il ritorno del nucleare
     * in Italia"); null se il tema non è una proposizione da sostenere o contestare ("la
     * sicurezza pubblica"). Assente negli intake precedenti alla Fase 4: vale `statement`.
     */
    stance_target?: string | null;
  };
  /**
   * Cosa chiede la domanda (C06): `tone` = il tono dei contenuti (sentiment Pulsar);
   * `stance` = la posizione delle persone verso un oggetto (favorevoli o contrari);
   * `both` = entrambe. Assente negli intake precedenti: vale `tone`.
   */
  measure?: 'tone' | 'stance' | 'both';
  /** Parti della domanda da coprire nel report (intake-v5); assenti negli intake precedenti. */
  requirements?: QuestionRequirement[];
  time_references: TimeReference[];
  comparison: { requested: boolean; description: string | null; second_term_available: boolean };
  segments_requested: string[];
  premise_check: string | null;
  /** Ambiguità che secondo il modello cambia la risposta (vagliata dal codice). */
  clarification: string | null;
  assumptions: string[];
}

export interface PendingQuestion {
  code: 'period_mismatch' | 'comparison_missing' | 'ambiguity';
  question: string;
  options: { id: 'proceed' | 'change_scope'; label: string }[];
}

export interface RunPlan {
  intent: Intent;
  secondary: Intent[];
  units: string;
  segmentation: string;
  baseline: string;
  enrichments: string[];
  lines: string[];
}

export interface ReportCaveat {
  text: string;
  flags: string[];
}

/**
 * Interpretazione o implicazione del report (decisione dell'utente del 2026-10-19): sta in
 * una sezione separata dalle osservazioni e poggia sempre su evidenze citate in `premises`
 * ({{m:…}}, {{c:…}}), con il passaggio che porta dalle evidenze alla lettura.
 */
export interface ReportInterpretation {
  kind: 'interpretazione' | 'implicazione';
  text: string;
  /** Evidenze su cui poggia, per riferimento, e il passaggio inferenziale. */
  premises: string;
}

/** Parte della domanda individuata dall'intake (Fase 6.1). */
export interface QuestionRequirement {
  id: string;
  text: string;
  /** I dati possono misurarla direttamente (si), solo in parte, o no (resta al più un'interpretazione). */
  measurable: 'si' | 'in_parte' | 'no';
  reason: string;
}

/** Come il report risponde a ogni parte della domanda. */
export interface RequirementCoverage {
  requirement_id: string;
  /** misurato: con osservazioni; interpretato: nelle interpretazioni, su evidenze; non_rispondibile: i dati non lo consentono. */
  status: 'misurato' | 'interpretato' | 'non_rispondibile';
  /** Sezione del report che risponde, o cosa servirebbe per rispondere. */
  where: string;
  reason: string | null;
}

export interface DraftReport {
  verdict: { text: string; signal: SignalClass; signal_basis: string; signal_reason: string; material_flags_cited: string[] };
  evidence: { text: string; baseline: string | null }[];
  top_3: { text: string }[] | null;
  closer_look: string | null;
  /** Report precedenti a draft-v7: le implicazioni ora stanno in `interpretations`. */
  so_what?: string | null;
  /** Interpretazioni e implicazioni fondate su evidenze (draft-v7); assenti nei report precedenti. */
  interpretations?: ReportInterpretation[];
  /** Copertura delle parti della domanda (draft-v7); assente nei report precedenti. */
  requirements_coverage?: RequirementCoverage[];
  caveats: ReportCaveat[];
  baseline_used: string;
  /** Livello della baseline del playbook (1 periodo precedente, 2 segmento interno, 3 andamento interno). */
  baseline_level: 1 | 2 | 3;
  charts: { type: 'sentiment_by_segment' | 'timeline' | 'source_mix' | 'narratives'; metric_refs: string[] }[];
}

export interface DataAnswer {
  answer: string;
  caveat: string | null;
}

export interface VerificationIssue {
  code: string;
  severity: 'error' | 'warning';
  message: string;
  /** Sezione o testo interessato. */
  where: string | null;
}

export interface ReportMetadata {
  intent: Intent;
  searches: { id: string; name: string | null }[];
  window_utc: [string, string];
  window_hours: number;
  rows_analyzed: number;
  stories_analyzed: number;
  rows_available: number | null;
  origin: 'pulsar_api' | 'export';
  fetch_mode: string;
  filters: string[];
  units: string;
  baseline: string | null;
  quality: { overall: string; flags: string[] };
  signal: SignalClass | null;
  computed_by: { prepare: string; metrics: string; prompts: string; knowledge: string; models: Record<string, string> };
}
